import { Modal, Text } from '@loomhq/lens';
import classnames from 'classnames';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { SHARE_VIDEO } from 'constants/checklist';
import { ADD_CUSTOM_VIEWERS_CTA_CLICKED } from 'constants/events';
import {
  VIDEO_INVITE_PEOPLE_ACTION,
  VIDEO_PASSWORD_PROTECT_ACTION,
  VIDEO_SHARE_PRIVATE_ACTION,
} from 'constants/scopes';
import * as analytics from 'utilities/analytics';

import { useCompleteChecklistItem } from 'hooks/checklist';

import CustomAccessSettings from '../custom-access-editor';
import PrivacySettings from '../privacy-settings';
import SalesforceShare from '../salesforce-share';
import PrivacySection from '../share-video-widget/privacy-section';

import Scopes from 'components/scopes';

import styles from './styles.module.less';

const DropdropContainer = ({ children, isFirstVideoShareModal }) => (
  <div
    className={classnames(
      'absolute right:0 bgc:background radius:medium border shadow:small',
      { [styles.firstVideoShareContainer]: isFirstVideoShareModal },
      { 'top:xlarge': !isFirstVideoShareModal }
    )}
    style={{ width: '360px', zIndex: 1 }}
  >
    {children}
  </div>
);

const ModalContainer = ({ children, closeModal }) =>
  createPortal(
    <Modal
      isOpen={true}
      onCloseClick={closeModal}
      onBackgroundClick={closeModal}
      maxWidth="360px"
    >
      {children}
    </Modal>,
    document.body
  );

export default ({
  closeModal,
  shouldOpenAsDropdown,
  video,
  isFirstVideoShareModal = false,
}) => {
  const [
    isShowingCustomAccessSettings,
    setIsShowingCustomAccessSettings,
  ] = useState(false);
  const [isShowingPrivacySettings, setIsShowingPrivacySettings] = useState(
    false
  );
  const { completeChecklistItem } = useCompleteChecklistItem(SHARE_VIDEO);

  const exitEditMode = () => {
    setIsShowingCustomAccessSettings(false);
    setIsShowingPrivacySettings(false);
  };

  const isEditMode = isShowingCustomAccessSettings || isShowingPrivacySettings;

  const onAddPasswordClick = () => setIsShowingPrivacySettings(true);
  const onCustomViewerClick = () => {
    analytics.track(ADD_CUSTOM_VIEWERS_CTA_CLICKED);

    setIsShowingCustomAccessSettings(true);
  };

  const Container = shouldOpenAsDropdown ? DropdropContainer : ModalContainer;

  const padding = shouldOpenAsDropdown ? 'py:xlarge px:xlarge' : '';

  return (
    <Container
      closeModal={closeModal}
      isFirstVideoShareModal={isFirstVideoShareModal}
    >
      {/*
      allow overflow when not in edit mode, because we want the embed UI
      to be able to render outside the container when in edit mode, e.g.
      if the user is adding lots of emails, we want them to scroll
      */}
      <div className={isEditMode ? 'relative' : ''}>
        {!isEditMode && (
          <div className={padding}>
            <Text variant="title">Share your video</Text>
            <Scopes
              names={[
                VIDEO_SHARE_PRIVATE_ACTION,
                VIDEO_PASSWORD_PROTECT_ACTION,
                VIDEO_INVITE_PEOPLE_ACTION,
              ]}
              some={true}
            >
              <PrivacySection
                video={video}
                onCustomViewerClick={onCustomViewerClick}
                onAddPasswordClick={onAddPasswordClick}
                isFirstVideoShareModal={isFirstVideoShareModal}
              />
            </Scopes>
            <SalesforceShare />
          </div>
        )}
        {isShowingCustomAccessSettings && (
          <CustomAccessSettings
            video={video}
            onSaveClick={exitEditMode}
            onExitClick={exitEditMode}
            completeChecklistItem={completeChecklistItem}
          />
        )}
        {isShowingPrivacySettings && (
          <PrivacySettings
            video={video}
            onSaveClick={exitEditMode}
            onCancelClick={exitEditMode}
          />
        )}
      </div>
    </Container>
  );
};
